const BEGINNER_HERO =
  'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1600&q=80';
const SPRINT_HERO =
  'https://images.unsplash.com/photo-1530549387789-4c1017266635?auto=format&fit=crop&w=1600&q=80';

const REFERENCE_LINKS = [
  {
    label: 'USA Triathlon — beginner resources',
    url: 'https://www.usatriathlon.org/resources/multisport-101',
  },
  {
    label: 'TrainingPeaks — what is a brick workout?',
    url: 'https://www.trainingpeaks.com/blog/what-is-a-brick-workout/',
  },
  {
    label: 'Ironman — sprint triathlon training tips',
    url: 'https://www.ironman.com/news/sprint-triathlon-training-tips',
  },
];

const SESSION_KEYS = [
  'Swim easy: continuous laps at RPE 3–4; long exhale underwater, relaxed catch.',
  'Swim technique: 4 × 50 m drill (catch-up, fingertip drag, kickboard) + easy 100–200 m between.',
  'Swim endurance: longest continuous swim of the week—add 25–50 m vs. prior week until week 2 peak.',
  'Bike easy: steady cadence 85–95 rpm; practice shifting and drinking on the bike.',
  'Bike steady: include short race-effort intervals (RPE 6) with easy recovery between.',
  'Run easy: conversational jog; walk breaks are fine in weeks 4–3 for beginners.',
  'Brick: bike then run within 10 min—first 3–5 min of the run should feel very easy.',
  'Rest day: walk, yoga, or complete rest.',
];

const RECOVERY_PANEL = {
  type: 'panel',
  title: 'Recovery rules for the whole month',
  list: [
    'Sleep 7–9 hours—this is when adaptation happens.',
    'One full rest day per week minimum; two during taper week.',
    'Foam roll quads, calves, and upper back after bricks.',
    'If illness or injury appears, skip the hard session—not the easy one—and resume when symptom-free 48 hrs.',
    'Never stack hard days back-to-back in weeks 4–2; the brick already combines two disciplines.',
  ],
};

const GEAR_PANEL = {
  type: 'panel',
  title: 'Gear & nutrition basics',
  list: [
    'Essentials: goggles (spare pair), cap, wetsuit if water is cold, helmet, bike shoes, run shoes, race belt, sunglasses.',
    'Training fuel: water on every bike over 30 min; practice one gel or banana in week 2 brick if race lasts over 75 min.',
    'Race morning: 16–20 oz fluid 2–3 hours before start; small familiar snack 60–90 min out if hungry.',
    'Transitions: lay gear left-to-right in order of use; elastic laces save time in T2.',
    'Ohio note: spring and fall races may need arm warmers or wetsuit—check water temp rules.',
  ],
};

/**
 * Beginner / super-sprint: 300 m swim · 7 mi bike · 1.5 mi run
 */
export const TRIATHLON_BEGINNER_ONE_MONTH_PLAN_POST = {
  id: 'featured-triathlon-beginner-one-month-plan',
  slug: 'triathlon-beginner-training-plan-one-month-before-race',
  title: 'Beginner Triathlon Plan: 4 Weeks Before Your First Race (300 m · 7 mi · 1.5 mi)',
  excerpt:
    'A gentle day-by-day swim, bike, run, and brick schedule for beginner or super-sprint races—300 m swim, 7-mile bike, 1.5-mile run. Not medical advice.',
  publishedAt: '2026-07-23T01:00:00.000Z',
  coverImageUrl: BEGINNER_HERO,
  authorName: 'Daily Sports Life',
  clubName: 'Endurance Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Ready for the next distance?',
  relatedPosts: [
    {
      slug: 'triathlon-sprint-training-plan-one-month-before-race',
      title: 'Sprint Triathlon Plan: 4 Weeks Before Race Day (750 m · 13 mi · 5K)',
      blurb: 'step up to sprint distance when you finish this plan.',
    },
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware',
      blurb: 'flat loops for easy brick runs.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Plan overview',
      items: [
        { value: '4 weeks', label: 'Race countdown' },
        { value: 'Beginner', label: '300 m · 7 mi · 1.5 mi' },
        { value: '3–4 hrs', label: 'Peak weekly volume' },
        { value: 'First race', label: 'Walk-run OK' },
      ],
    },
    {
      type: 'panel',
      title: 'Who this plan is for',
      paragraphs: [
        'This plan targets beginner or super-sprint triathlons: 300 m (about 12 pool lengths) swim, 7-mile bike, and 1.5-mile run—common at community “try-a-tri” and youth-friendly events.',
        'Before week 4 you should be able to swim 150 m with rest, bike 30 minutes, and walk-jog 15 minutes. No prior triathlon experience required.',
        'Get medical clearance before starting if you have heart disease, uncontrolled blood pressure, diabetes, joint injuries, or have been mostly inactive. Stop for chest pain, dizziness, or sharp pain and seek care.',
      ],
      chips: ['Beginner distance', 'Super-sprint', 'Brick workouts', 'Taper included'],
    },
    {
      type: 'panel',
      title: 'How the four weeks are organized',
      orderedList: [
        'Week 4: Build the habit—two to three sessions per sport, mostly easy. One very short brick.',
        'Week 3: Slightly longer swim and bike; brick run approaches race distance.',
        'Week 2 (peak): Longest sessions—swim past race distance, bike ~10 mi steady, run 1.5 mi off the bike.',
        'Week 1 (taper): Cut volume 40–50%; arrive rested and confident.',
      ],
      callout:
        'RPE guide: Easy = you can talk in full sentences. Moderate = breathing harder but controlled. Hard = short bursts only.',
    },
    {
      type: 'panel',
      title: 'Session keys — what to actually do',
      list: SESSION_KEYS,
    },
    {
      type: 'table',
      title: 'Week 4 — Four weeks to race (foundation)',
      intro: 'Goal: finish every session. Walk breaks on runs are encouraged. Keep it fun.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        ['Monday', 'Rest or walk', '20 min', 'Easy walk or gentle stretching.'],
        ['Tuesday', 'Swim — technique', '25 min', '100 m warm-up · 4 × 25 m drill/rest · 100 m cool-down.'],
        ['Wednesday', 'Bike easy', '30 min', 'Flat neighborhood loop; practice helmet and shoes.'],
        ['Thursday', 'Run walk-jog', '20 min', '1 min jog / 1 min walk × 10, or easy continuous jog.'],
        ['Friday', 'Swim — endurance', '25 min', '250–300 m total (rest 30 sec at wall every 50 m if needed).'],
        ['Saturday', 'Brick (intro)', '40 min total', 'Bike 25 min easy → run 10 min very easy (walk first 2 min OK).'],
        ['Sunday', 'Bike easy', '35 min', 'Conversational pace; practice drinking from bottle.'],
      ],
    },
    {
      type: 'table',
      title: 'Week 3 — Three weeks to race (build)',
      intro: 'Goal: swim the race distance once; bike a little farther than 7 mi in training.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        ['Monday', 'Rest', '—', 'Full rest or 10 min mobility.'],
        ['Tuesday', 'Swim — endurance', '30 min', '300 m continuous OR 3 × 100 m steady, 45 sec rest.'],
        ['Wednesday', 'Bike steady', '40 min', '3 × 4 min moderate (RPE 6), 3 min easy between.'],
        ['Thursday', 'Run easy', '22 min', 'Continuous easy jog; 4 × 15 sec relaxed strides.'],
        ['Friday', 'Swim — technique', '25 min', '200 m warm-up · 6 × 25 m drill · 100 m easy.'],
        ['Saturday', 'Brick (medium)', '50 min total', 'Bike 35 min (last 5 min moderate) → run 12 min (walk 1 min if needed).'],
        ['Sunday', 'Run easy + core', '30 min', '20 min jog · 10 min planks and glute bridges.'],
      ],
    },
    {
      type: 'table',
      title: 'Week 2 — Two weeks to race (peak week)',
      intro: 'Goal: cover race distances in training. One race-simulation brick.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        ['Monday', 'Rest', '—', 'Hydrate; light stretching.'],
        ['Tuesday', 'Swim — peak', '30–35 min', '400 m total; include 300 m continuous once.'],
        ['Wednesday', 'Bike — long ride', '50–55 min', '~10–11 mi easy; last 8 min at steady race effort.'],
        ['Thursday', 'Run — steady', '25 min', '8 min easy · 1.5 mi moderate · 5 min easy cool-down.'],
        ['Friday', 'Swim easy', '20 min', 'Recovery swim—smooth strokes only.'],
        ['Saturday', 'Brick (race simulation)', '60 min total', 'Bike 40 min (middle 10 min race pace) → run 15 min (first 3 min walk OK).'],
        ['Sunday', 'Easy walk or spin', '25 min', 'Very light movement only.'],
      ],
    },
    {
      type: 'table',
      title: 'Week 1 — Race week (taper)',
      intro: 'Goal: rest, confidence, and a smooth race morning.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        ['Monday', 'Swim easy', '15 min', '200 m smooth; 2 × 25 m easy pickups.'],
        ['Tuesday', 'Bike easy', '20 min', 'Easy spin; 2 × 1 min moderate pickups.'],
        ['Wednesday', 'Run easy', '15 min', '10 min jog · 3 × 15 sec strides · walk cool-down.'],
        ['Thursday', 'Rest', '—', 'Pack gear; check bike tires and brakes.'],
        ['Friday', 'Optional shake-out', '10 min', 'Swim 8 min OR walk 10 min—pick one.'],
        ['Saturday', 'Rest', '—', 'Familiar meals, early bed, visualize transitions.'],
        ['Sunday', 'Race day', '—', 'See race-morning checklist below. You are ready.'],
      ],
    },
    {
      type: 'panel',
      title: 'Race-morning checklist (beginner)',
      orderedList: [
        'Wake 2.5–3 hours before start; light breakfast you have eaten in training.',
        'Arrive 60–75 min early; rack bike, lay out run gear, helmet on bars.',
        'Swim: start at the back or side if nervous; breathe every two strokes.',
        'Bike: easy first 5 min; you trained ~10 mi—7 mi will feel manageable.',
        'Run: walk 30–60 sec at T2 if legs feel heavy—that is normal.',
        'Finish with a smile—your first triathlon is a win.',
      ],
    },
    GEAR_PANEL,
    RECOVERY_PANEL,
  ],
  referenceLinks: REFERENCE_LINKS,
};

/**
 * Sprint: 750 m swim · 13 mi bike · 5K run
 */
export const TRIATHLON_SPRINT_ONE_MONTH_PLAN_POST = {
  id: 'featured-triathlon-sprint-one-month-plan',
  slug: 'triathlon-sprint-training-plan-one-month-before-race',
  title: 'Sprint Triathlon Plan: 4 Weeks Before Race Day (750 m · 13 mi · 5K)',
  excerpt:
    'A day-by-day swim, bike, run, and brick schedule for the final month before a sprint triathlon—750 m swim, 13-mile bike, 5K run—plus taper week and race-day steps. Not medical advice.',
  publishedAt: '2026-07-23T01:05:00.000Z',
  coverImageUrl: SPRINT_HERO,
  authorName: 'Daily Sports Life',
  clubName: 'Endurance Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More training guides',
  relatedPosts: [
    {
      slug: 'triathlon-beginner-training-plan-one-month-before-race',
      title: 'Beginner Triathlon Plan (300 m · 7 mi · 1.5 mi)',
      blurb: 'shorter distance if this is your first multisport race.',
    },
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware',
      blurb: 'brick-run loops and open space for easy jogs.',
    },
    {
      slug: 'open-tennis-courts-powell-dublin-polaris-delaware-ohio',
      title: 'Free Tennis Courts Near Powell, Dublin, Polaris & Delaware',
      blurb: 'cross-training between hard sessions.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Plan overview',
      items: [
        { value: '4 weeks', label: 'Race countdown' },
        { value: 'Sprint', label: '750 m · 13 mi · 5K' },
        { value: '5–6 hrs', label: 'Peak weekly volume' },
        { value: '3 sports', label: 'Swim · bike · run' },
      ],
    },
    {
      type: 'panel',
      title: 'Who this plan is for',
      paragraphs: [
        'This plan is for the final four weeks before a sprint-distance triathlon: 750 m swim, 13-mile bike, and 5K (3.1 mi) run.',
        'You should already swim 400 m non-stop, bike 50 minutes, and run 25 minutes before starting week 4. Completed a beginner/super-sprint race? This is your natural next step.',
        'Get medical clearance before starting if you have heart disease, uncontrolled blood pressure, diabetes, joint injuries, or have been mostly inactive. Stop for chest pain, dizziness, or sharp pain and seek care.',
      ],
      chips: ['Sprint distance', 'Brick workouts', 'Race-pace reps', 'Taper included'],
    },
    {
      type: 'panel',
      title: 'How the four weeks are organized',
      orderedList: [
        'Week 4: Build consistency—three swims, three bikes, three runs. One short brick.',
        'Week 3: Add duration. Longer bike (~11 mi), longer swim, and a medium brick.',
        'Week 2 (peak week): Longest sessions—~15 mi bike, 900 m+ swim, race-simulation brick.',
        'Week 1 (race week): Taper—cut volume 40–50%, stay sharp, execute your race plan.',
      ],
      callout:
        'RPE guide: Easy = conversational pace. Moderate = breathing harder but controlled. Hard = short intervals only—not all-out except brief race-pace reps.',
    },
    {
      type: 'panel',
      title: 'Session keys — what to actually do',
      list: SESSION_KEYS,
    },
    {
      type: 'table',
      title: 'Week 4 — Four weeks to race (foundation)',
      intro: 'Goal: hit every discipline three times. Keep most work easy.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        ['Monday', 'Rest or walk', '20–30 min', 'Optional easy walk or mobility.'],
        ['Tuesday', 'Swim — technique', '30–35 min', '400 m warm-up · 6 × 50 m drill/rest · 200 m cool-down.'],
        ['Wednesday', 'Bike easy', '40–45 min', '~8–9 mi flat; practice shifting and hydration.'],
        ['Thursday', 'Run easy', '25–30 min', 'Conversational pace; 4 × 20 sec relaxed strides.'],
        ['Friday', 'Swim — endurance', '35–40 min', '600–750 m total (rest 30 sec every 200 m if needed).'],
        ['Saturday', 'Brick (short)', '55 min total', 'Bike 35 min easy (~7 mi) → run 15 min easy.'],
        ['Sunday', 'Bike steady', '50–55 min', '~10–11 mi; 3 × 5 min moderate (RPE 6), 3 min easy between.'],
      ],
    },
    {
      type: 'table',
      title: 'Week 3 — Three weeks to race (build)',
      intro: 'Goal: extend longest swim and bike. Brick run should feel familiar.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        ['Monday', 'Rest', '—', 'Full rest or 15 min stretching.'],
        ['Tuesday', 'Swim — endurance', '40–45 min', '800–900 m continuous or 4 × 200 m steady, 30 sec rest.'],
        ['Wednesday', 'Bike steady', '55–60 min', '~11–12 mi; 4 × 6 min at race effort, 3 min easy between.'],
        ['Thursday', 'Run — steady', '30–35 min', '10 min easy · 3 × 5 min moderate · 5 min cool-down.'],
        ['Friday', 'Swim — technique + speed', '35 min', '300 m warm-up · 8 × 50 m moderate (45 sec rest) · 200 m easy.'],
        ['Saturday', 'Brick (medium)', '70 min total', 'Bike 45 min (~10 mi, last 8 min race pace) → run 20 min (first 5 min very easy).'],
        ['Sunday', 'Run easy + core', '35 min', '25 min easy jog · 10 min planks, dead bugs, glute bridges.'],
      ],
    },
    {
      type: 'table',
      title: 'Week 2 — Two weeks to race (peak week)',
      intro: 'Goal: longest sessions of the plan. Practice race nutrition and transitions once.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        ['Monday', 'Rest', '—', 'Recovery from weekend; hydrate well.'],
        ['Tuesday', 'Swim — peak endurance', '45–50 min', '1,000–1,100 m total; include 2 × 400 m steady with 1 min rest.'],
        ['Wednesday', 'Bike — long ride', '70–80 min', '~14–16 mi mostly easy; last 12 min at race effort. Drink every 15–20 min.'],
        ['Thursday', 'Run — race pace', '35 min', '10 min easy · 2 × 1 mi at goal 5K pace · 5 min easy between · cool-down.'],
        ['Friday', 'Swim easy', '25–30 min', 'Recovery swim—smooth strokes only.'],
        ['Saturday', 'Brick (race simulation)', '85 min total', 'Bike 55 min (~13 mi, middle 15 min race pace) → run 25 min (first 8 min easy).'],
        ['Sunday', 'Easy spin or walk', '30 min', 'Flush legs—very light bike or walk only.'],
      ],
    },
    {
      type: 'table',
      title: 'Week 1 — Race week (taper)',
      intro: 'Goal: arrive rested. Short, sharp sessions only. Lay out gear by Thursday.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        ['Monday', 'Swim easy', '20 min', '500 m smooth; 4 × 25 m race-pace pickups.'],
        ['Tuesday', 'Bike easy + 3 pickups', '30 min', '20 min easy · 3 × 1 min moderate · cool-down.'],
        ['Wednesday', 'Run easy', '20 min', '15 min jog · 4 × 15 sec strides · walk cool-down.'],
        ['Thursday', 'Rest', '—', 'Pack transition bag; check bike tires, chain, and brakes.'],
        ['Friday', 'Optional shake-out', '15 min', 'Swim 10 min OR bike 10 min OR walk—pick one.'],
        ['Saturday', 'Rest', '—', 'Carb-focused meals, early bed, visualize swim start and transitions.'],
        ['Sunday', 'Race day', '—', 'See race-morning checklist below. Trust your training.'],
      ],
    },
    {
      type: 'panel',
      title: 'Race-morning checklist (sprint)',
      orderedList: [
        'Wake 3 hours before start; light familiar breakfast (toast, banana, oatmeal—nothing new).',
        'Arrive 75–90 min early; rack bike, rack run shoes, helmet on bars, bib pinned.',
        'Swim warm-up: 5–8 min easy swimming if allowed; stay calm in the start corral.',
        'Bike: hold back first 10 min; drink early—you trained ~15 mi, race is 13 mi.',
        'Run: start 15–30 sec/mile slower than standalone 5K pace for the first half mile.',
        'Finish strong—save one gear for the last 400 m.',
      ],
    },
    GEAR_PANEL,
    RECOVERY_PANEL,
  ],
  referenceLinks: REFERENCE_LINKS,
};

/** @deprecated Use TRIATHLON_SPRINT_ONE_MONTH_PLAN_POST — kept for slug alias */
export const TRIATHLON_ONE_MONTH_PLAN_POST = TRIATHLON_SPRINT_ONE_MONTH_PLAN_POST;

export const TRIATHLON_SLUG_ALIASES = {
  'triathlon-training-plan-one-month-before-race': 'triathlon-sprint-training-plan-one-month-before-race',
};
